<script setup lang="ts">
//#region imports
import { cloneDeep, get, isEmpty } from 'lodash-es';
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useError } from '@/composables/useError';
import { getLiter } from '@/views/CRM/helpers/getLiter';
import { useCrmMetrics } from '@/views/CRM/composables/useCrmMetrics';
import formatColumn from '@/views/Dashboard/helper/customFormat';
import AddButton from '@/components/AddButton.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import type { CrmActionGroup, CrmActivityMetric, CrmActivityMetricName, EditableCrmActivity } from '@/types/crm';
import type { DateRange } from '@/types/core';
//#endregion

//#region types
interface Props {
  canEdit?: boolean;
  previewMode?: boolean | '';
  activity?: EditableCrmActivity;
  controlGroupActive?: boolean;
  metricValues?: CrmActivityMetric[];
  errors?: Record<string, any>;
}

interface Emits {
  'change-activity-range': [date: DateRange | null];
}

type ActivityMetricListItem = CrmActivityMetricName | 'special-add' | 'special-empty';
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  canEdit: false,
  previewMode: false,
  activity: () => ({} as EditableCrmActivity),
  controlGroupActive: true,
  metricValues: () => [],
  errors: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const store = useTypedStore();
const { t, locale } = useI18n();
const instance = getCurrentInstance();
const proxy = instance?.proxy as any;
const { getError } = useError({ errors: computed(() => props.errors || {}) });
const { crmMetrics, metricsIndex } = useCrmMetrics();
//#endregion

//#region state
const pEdit = ref(false);
const pMetrics = ref<CrmActivityMetricName[]>([]);
const isLoading = ref(false);
const metricsDateRange = ref<any[]>([]);
const isEditMetricsDate = ref(false);
//#endregion

//#region computed
const errorsDetailed = computed(() => [metricsCooldownError.value].filter((item) => !!item));
const metricsCooldownError = computed(() => getError('metrics_cooldown'));

const cooldownStats = computed(() => {
  const result: Array<{ key: string; icon: string; text: string }> = [];

  if (props.activity.metrics_has_cooldown) {
    const metricsCooldown = props.activity.metrics_cooldown || 0;

    result.push({
      key: 'cooldown',
      icon: 'history',
      text: t('crm.activities.metrics.cooldown_stat', { cooldown: metricsCooldown }, metricsCooldown),
    });
  }

  if (props.activity.metrics_updated_at) {
    result.push({
      key: 'updated-at',
      icon: 'clock',
      text: t('crm.activities.metrics.updated_at_stat', {
        datetime: proxy?.$filters?.formatTimeDate(props.activity.metrics_updated_at),
      }),
    });
  }

  return result;
});

const pPreviewMode = computed(() => props.previewMode || props.previewMode === '');
const edit = computed(() => pPreviewMode.value && pEdit.value);

const metrics = computed<ActivityMetricListItem[]>(() => (edit.value ? pMetrics.value : props.activity.metrics || []));
const keepHeight = computed(() => false);
const actionGroupsStats = computed<CrmActionGroup[]>(() => props.activity.action_groups || []);

const actionGroupsStatsSorted = computed(() => {
  return actionGroupsStats.value
    .filter((item) => props.controlGroupActive || !item.is_control)
    .map((value, index: number) => ({
      value,
      index,
    }));
});

const actionGroupsStatsIndex = computed(() => {
  const result: Record<string, number> = {};

  actionGroupsStatsSorted.value.forEach((actionGroup: any, index: number) => {
    if (actionGroup && actionGroup.value && actionGroup.value.id) {
      result[actionGroup.value.id] = index;
    }
  });

  return result;
});

const pMetricValues = computed(() => {
  const metricValuesMap: Record<string, any[]> = {};

  props.metricValues.forEach((metricValue) => {
    const result = metricValue.values.map((item: any) => {
      if (pPreviewMode.value) {
        const percent = typeof item.percent !== 'undefined' ? item.percent : 0;

        return {
          id: item.crm_action_group_id,
          value: formatColumn(item.value, get(metricsIndex.value, [metricValue.name, 'column_format'], 'number')),
          percent: formatColumn(percent, 'percent'),
          growth: percent,
          playersCount: formatColumn(item.players_count, 'number'),
          playersPercent: formatColumn(item.players_percent, 'percent'),
          playersTotalCount: formatColumn(item.players_total_count, 'number'),
        };
      }

      return getMetricValueStub(item.crm_action_group_id);
    });

    result.sort((a: any, b: any) => {
      const aIndex = actionGroupsStatsIndex.value[a.id] ?? Number.MAX_SAFE_INTEGER;
      const bIndex = actionGroupsStatsIndex.value[b.id] ?? Number.MAX_SAFE_INTEGER;

      if (aIndex < bIndex) {
        return -1;
      }
      if (aIndex > bIndex) {
        return 1;
      }
      return 0;
    });

    metricValuesMap[metricValue.name] = result;
  });

  return metricValuesMap;
});

const computedMetrics = computed(() => {
  const baseMetrics = metrics.value;
  const result: ActivityMetricListItem[] = !baseMetrics.length
    ? [...baseMetrics, (edit.value || !props.previewMode ? 'special-add' : 'special-empty')]
    : [...baseMetrics];

  return result.map((metric) => {
    const actionGroups = getActionGroupMetrics(metric);
    const actionGroupsLeaderMeta = actionGroups.reduce((acc: any, item: any, index: number) => {
      return (!item.value.is_control || props.controlGroupActive) && item.metricValues.percent > acc.value
        ? { index, value: item.metricValues.percent }
        : acc;
    }, {
      index: -1,
      value: Number.MIN_SAFE_INTEGER,
    });

    const isActionGroupsEmpty = actionGroups.filter((item: any) => !item.metricValues.isEmpty).length <= 0;

    return {
      name: metric,
      title: !['special-add', 'special-empty'].includes(metric)
        ? get(metricsIndex.value, [metric, 'title'], metric)
        : '',
      actionGroups,
      leader: actionGroupsLeaderMeta.index,
      isActionGroupsEmpty,
    };
  });
});

const isMetricsLoaded = computed(() => !isEmpty(crmMetrics.value));
//#endregion

//#region methods
function onChangeActivityRange(date: DateRange | null) {
  emit('change-activity-range', date);
}

function getMetricValueStub(id = '') {
  return {
    id,
    value: 0,
    percent: 0,
    growth: 0,
    isEmpty: true,
  };
}

function getActionGroupMetrics(metric: ActivityMetricListItem) {
  return actionGroupsStatsSorted.value.map((value, index) => ({
    ...value,
    value: {
      ...value.value,
      players_count: formatColumn(value.value.players_count, 'number'),
    },
    metricValues: getMetricValues(index, metric),
  }));
}

function getMetricValues(actionGroupIndex: number, metric: ActivityMetricListItem) {
  return get(pMetricValues.value, [metric, actionGroupIndex], getMetricValueStub());
}

function handleEdit() {
  const next = !pEdit.value;
  pEdit.value = next;
  pMetrics.value = next ? cloneDeep(props.activity.metrics) : [];
}

function handleAddMetricPopupOpen() {
  popupManager.activityMetrics.open({
    data: {
      selectedMetrics: metrics.value,
    },
    listeners: {
      select: handleAddMetric,
    },
  });
}

function handleAddMetric(metricsValue: string[]) {
  const copy = cloneDeep(metricsValue) as CrmActivityMetricName[];
  if (edit.value) {
    pMetrics.value = copy;
  } else {
    props.activity.metrics = copy;
  }
}

function handleDeleteMetric(name: ActivityMetricListItem) {
  if (name === 'special-add' || name === 'special-empty') {
    return;
  }

  const index = metrics.value.findIndex((metric) => metric === name);
  metrics.value.splice(index, 1);
}

function handleSave() {
  props.activity.metrics = cloneDeep(pMetrics.value);
  isLoading.value = true;

  api.updateActivityMetrics(props.activity.id, props.activity.metrics)
    .then(() => {
      handleEdit();
    })
    .finally(() => {
      isLoading.value = false;
    });
}
//#endregion

//#region watch
watch(locale, async () => {
  await store.dispatch('crm/getMetrics');
}, { immediate: true });
//#endregion
</script>

<template>
  <div
    v-loading="!isMetricsLoaded"
    class="crm-activity-metrics t:space-y-16"
  >
    <FormRow
      class="crm-activity-metrics__form-row"
      :error="errorsDetailed.length > 0"
    >
      <div class="t:flex t:justify-between">
        <UiLabel
          :label="$t('crm.activities.metrics.title')"
          :font-size="20"
          bold
        />
        <div class="t:flex t:items-center t:gap-16">
          <template
            v-if="pPreviewMode"
          >
            <div class="t:flex t:gap-8">
              <div
                v-if="cooldownStats.length > 0"
                class="t:flex t:items-center t:space-x-16"
              >
                <div
                  v-for="stats in cooldownStats"
                  :key="stats.key"
                  class="crm-activity-metrics__cooldown-preview-param t:space-x-8"
                >
                  <UiIcon
                    color="var(--text-body-secondary)"
                    :name="stats.icon"
                  />
                  <div v-html="stats.text" />
                </div>
              </div>
              <template v-if="isEditMetricsDate">
                <UiDatePicker
                  v-model="metricsDateRange"
                  :max-date="props.activity.activity_period_end"
                  :min-date="props.activity.activity_period_start"
                  :placeholder="$t(`tools.match_popup.placeholder_date_time`)"
                  :always-clearable="false"
                  teleport
                  :config="{
                    arrowLeft: '50%'
                  }"
                  :multi-calendars="{
                    static: false
                  }"
                  auto-apply
                  custom-input
                  @update:model-value="onChangeActivityRange"
                />
                <UiButton
                  icon="times"
                  type="negative"
                  is-icon
                  transparent-background
                  :disabled="isLoading"
                  @click="isEditMetricsDate = !isEditMetricsDate; onChangeActivityRange(null)"
                />
              </template>
              <template v-else>
                <UiButton
                  icon="calendar-alt"
                  is-icon
                  transparent-background
                  :disabled="isLoading"
                  @click="isEditMetricsDate = !isEditMetricsDate"
                />
              </template>
              <UiButton
                v-if="!edit && canEdit"
                key="edit"
                icon="pen"
                :disabled="isLoading"
                transparent-background
                is-icon
                @click="handleEdit"
              />
              <template v-if="edit">
                <UiButton
                  key="save"
                  icon="check"
                  :disabled="isLoading"
                  transparent-background
                  @click="handleSave"
                >
                  {{ $t('crm.buttons.save') }}
                </UiButton>
                <UiButton
                  key="cancel"
                  icon="times"
                  type="negative"
                  :disabled="isLoading"
                  transparent-background
                  @click="handleEdit"
                >
                  {{ $t('crm.buttons.cancel') }}
                </UiButton>
              </template>
            </div>
          </template>
          <template
            v-else
          >
            <div class="t:flex t:items-center t:gap-8">
              <UiLabel
                :label="$t('crm.activities.metrics.cooldown')"
                :font-size="16"
                :hint="$t(`crm.activities.metrics.cooldown_tooltip`)"
                bold
              />
              <UiSwitch
                v-model="props.activity.metrics_has_cooldown"
              />
            </div>
            <UiSeparator
              type="vertical"
            />
            <div class="t:flex t:items-center t:gap-8">
              <UiInput
                v-model="props.activity.metrics_cooldown"
                width="48px"
                :disabled="!props.activity.metrics_has_cooldown"
                :input-mask="'######'"
                :is-error="!!metricsCooldownError"
              />
              <UiLabel
                :label="$t('crm.activities.metrics.days')"
              />
              <CrmErrorPanel
                v-if="metricsCooldownError"
              >
                {{ metricsCooldownError }}
              </CrmErrorPanel>
            </div>
          </template>
          <UiButton
            v-if="edit || !pPreviewMode"
            key="addMetric"
            transparent-background
            icon="plus"
            :disabled="isLoading"
            @click="handleAddMetricPopupOpen"
          >
            {{ $t('crm.activities.info.add_metric') }}
          </UiButton>
        </div>
      </div>
    </FormRow>
    <div
      :class="{
        'crm-activity-metrics__group-list': true,
        'crm-activity-metrics__group-list_empty': computedMetrics[0]?.name === 'special-empty',
        'crm-activity-metrics__group-list_keep-height': keepHeight || !isMetricsLoaded,
        't:mb-16': true,
      }"
    >
      <template v-if="isMetricsLoaded">
        <template v-for="(metric, j) in computedMetrics">
          <AddButton
            v-if="metric.name === 'special-add'"
            :key="`special-add-${j}`"
            class="crm-activity-metrics__metric-card-add"
            @click="handleAddMetricPopupOpen"
          >
            {{ $t('crm.activities.info.add_metric') }}
          </AddButton>
          <div
            v-else-if="metric.name === 'special-empty'"
            :key="`special-empty-${j}`"
            class="crm-activity-metrics__no-metrics-panel"
          >
            <div class="t:flex t:items-center t:gap-8">
              <UiIcon
                color="inherit"
                name="info-circle"
              />
              {{ $t('crm.activities.metrics.no_metrics_selected_error') }}
            </div>
          </div>
          <FormRow
            v-else
            :key="`other-${j}`"
          >
            <div class="t:space-y-12">
              <div class="t:flex t:items-center t:justify-between">
                <UiLabel
                  :label="metric.title"
                  :font-size="14"
                  bold
                />
                <ActionIcon
                  v-if="edit || !pPreviewMode"
                  :tooltip="$t('crm.tooltips.delete')"
                  icon="trash-alt"
                  color="var(--text-negative)"
                  :disabled="isLoading"
                  @click="handleDeleteMetric(metric.name)"
                />
              </div>
              <template v-if="!metric.isActionGroupsEmpty">
                <template
                  v-for="(action, index) in metric.actionGroups"
                  :key="index"
                >
                  <div class="t:space-y-4">
                    <div class="t:flex t:items-stretch t:gap-8">
                      <UiLabel
                        :label="action.metricValues.value"
                        bold
                      />
                      <UiSeparator
                        color="--border-default"
                        type="vertical"
                      />
                      <div
                        :class="[
                          'crm-activity-metrics__metric-text',
                          `crm-activity-metrics__metric-text_${action.metricValues.growth >= 0
                            ? 'positive'
                            : 'negative'
                          }`
                        ]"
                      >
                        <UiIcon
                          :name="action.metricValues.growth >= 0 ? 'long-arrow-up' : 'long-arrow-down'"
                          color="inherit"
                          :size="14"
                        />
                        {{ action.metricValues.percent }}
                      </div>
                    </div>
                    <div class="t:flex t:items-stretch t:space-x-8">
                      <UiIcon
                        name="user"
                        color="var(--text-body-tertiary)"
                        :size="10"
                        class="t:mr-4"
                      />
                      <UiLabel
                        :label="`${action.metricValues.playersCount} (${action.metricValues.playersPercent})`"
                        :font-size="12"
                      />
                      <UiSeparator
                        color="--border-default"
                        type="vertical"
                      />
                      <UiLabel
                        :label="action.metricValues.playersTotalCount"
                        :font-size="12"
                        :hint="$t('crm.activities.metrics.players_tooltip')"
                      />
                    </div>
                    <div class="crm-activity-metrics__metric-group-name">
                      <template v-if="action.value.is_control">
                        {{ $t('crm.activities.actions.control_group') }}
                      </template>
                      <template v-else>
                        {{ $t('crm.activities.actions.group') }} {{ getLiter(+index - (controlGroupActive ? 1 : 0)) }}
                      </template>
                    </div>
                  </div>
                  <UiSeparator
                    v-if="+index < metric.actionGroups.length - 1"
                    :key="index"
                    class="t:mx-8"
                  />
                </template>
              </template>
              <div
                v-else
                class="crm-activity-metrics__metric-no-data"
              >
                <UiIcon
                  color="inherit"
                  name="info-circle"
                />
                {{ $t('crm.activities.metrics.no_avaliable_data_error') }}
              </div>
            </div>
          </FormRow>
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.crm-activity-metrics {
  &__form-row .form-row__wrapper {
    padding: 16px 24px;
  }
  &__cooldown-preview-param {
    display: flex;
    color: var(--text-body);
    font-size: 12px;
  }
  &__no-metrics-panel{
    width: 100%;
    align-items: center;
    justify-content: center;
    display: flex;
    color: var(--text-body-secondary);
    font-size: 12px;
  }
  &__metric-card-add {
    min-height: 118px;
  }
  &__group-list {
    gap: 16px;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    display: grid;
    &_empty {
      display: flex;
    }
    @media (max-width: 2560px) {
      grid-template-columns: repeat(6, minmax(0, 1fr));
    }

    @media (max-width: 1950px) {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }

    @media (max-width: 1400px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    &_keep-height {
      min-height: 200px;
    }
  }
  &__metric-text {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    line-height: 120%; /* 16.8px */
    &_positive {
      color: var(--text-positive);
    }
    &_negative {
      color: var(--text-negative);
    }
  }
  &__metric-group-name {
    color: var(--text-body-secondary);
    font-size: 11px;
    font-weight: 500;
    line-height: 120%; /* 13.2px */
  }
  &__metric-no-data {
    align-items: center;
    display: flex;
    color: var(--text-body-secondary);
    font-size: 12px;
    gap: 4px;
  }
}
</style>
